import { useGetCurrentUserProfile } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { DepositButton } from '@/components/atoms/DepositButton/DepositButton'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { useSession } from '@/context/SessionProvider'
import { useGeoLocation } from '@/hooks/useGeoLocation'
import { Activity } from '@/icons'
import { Menu, MenuButton, Flex, MenuList, MenuItem, Icon, Box, Text } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { AiOutlineUser } from 'react-icons/ai'
import { BiSolidKey, BiLogOut } from 'react-icons/bi'
import { FiActivity } from 'react-icons/fi'
import { BsController } from 'react-icons/bs'
// import { walletStrategy } from '@/context/SessionProvider'
import TicketStartIcon from '@/public/assets/icons/ticket-star.svg'
import SendTipIcon from '@/public/assets/icons/get-money.svg'

export const UserProfile = () => {
  const { account, logout } = useSession()
  const { restricted } = useGeoLocation()

  const { data: user } = useGetCurrentUserProfile()

  const modal = useAppModal()

  const { t } = useTranslation()

  const canExportPrivateKey = account?.wallet.startsWith('social-') || account?.wallet == 'email'

  const handleExportPrivateKey = async () => {
    if (!account) return

    modal.openModalNew(AppModals.ExportThirdWebPrivateKeyModal, {})
  }

  return (
    <Menu>
      <MenuButton h={12} w={12}>
        <Flex justifyContent={'center'}>
          <AvatarCircle offset={2} boxSize="36px" src={user?.profile.avatar || ''} />
        </Flex>
      </MenuButton>
      <MenuList zIndex={60} w={280} px={6} py={4} mt={2} bg={'ninja.600'} border={0} rounded={'2xl'}>
        <Flex direction="column" gap={2} mb="2" alignItems={'center'}>
          <Box>
            <AvatarCircle offset={2} size="xl" src={user?.profile.avatar || ''} />
          </Box>
          <Text fontSize="sm"> {user?.profile.user_name}</Text>
        </Flex>
        <Box p={2} pl={3} mb="1" bg="ninja.900" rounded="xl" fontSize={'sm'}>
          <CopiableLink maxLength={16} value={account?.ethAddress!} />
        </Box>
        <Box p={2} pl={3} mb="2" bg="ninja.900" rounded="xl" fontSize={'sm'}>
          <CopiableLink maxLength={16} value={account?.injectiveAddress!} />
        </Box>
        <MenuItem mb="2" onClick={() => modal.openModalNew(AppModals.Account)} px={0} bg={'transparent'}>
          <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
            <Icon as={AiOutlineUser} boxSize={'5'} />
            <Text fontSize={'sm'}>{t('navbar.items.account')}</Text>
          </Flex>
        </MenuItem>
        <MenuItem
          mb="1"
          onClick={() => modal.openModalNew(AppModals.UserStats, { address: account?.injectiveAddress || '' })}
          px={0}
          bg={'transparent'}
        >
          <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
            <Activity />
            <Text fontSize={'sm'}>{t('navbar.items.statistics')}</Text>
          </Flex>
        </MenuItem>
        <MenuItem mb="1" px={0} bg={'transparent'}>
          <Link href={'/activity/' + account?.injectiveAddress}>
            <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
              <FiActivity size={20} />
              <Text fontSize={'sm'}>{t('common.activity')}</Text>
            </Flex>
          </Link>
        </MenuItem>
        <MenuItem mb="1" px={0} bg={'transparent'}>
          <Link href="/game-zone/history">
            <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
              <BsController size={20} />
              <Text fontSize={'sm'}>{t('game-zone.history-title')}</Text>
            </Flex>
          </Link>
        </MenuItem>
        <MenuItem mb="1" onClick={() => modal.openModalNew(AppModals.PromoCodeModal)} px={0} bg={'transparent'}>
          <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
            <TicketStartIcon />
            <Text fontSize={'sm'}>{t('common.promo-code')}</Text>
          </Flex>
        </MenuItem>
        <MenuItem mb="1" onClick={() => modal.openModalNew(AppModals.SendTip)} px={0} bg={'transparent'}>
          <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
            <Icon as={SendTipIcon} w={5} h={5} />
            <Text fontSize={'sm'}>{t('common.send-tip')}</Text>
          </Flex>
        </MenuItem>
        {canExportPrivateKey && (
          <MenuItem mb="1" onClick={handleExportPrivateKey} px={0} bg={'transparent'}>
            <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
              <BiSolidKey size={20} />
              <Text fontSize={'sm'}>{t('common.export-private-key')}</Text>
            </Flex>
          </MenuItem>
        )}
        <MenuItem as={Box} display={'block'} px={0} bg={'transparent'}>
          <DepositButton
            styles={{
              rounded: 'xl',
              w: 'full',
            }}
            mobile={false}
            text={t('common.deposit-withdraw')}
          />
          {restricted && (
            <Text display={{ base: 'block', xl: ' none' }} fontSize={'xs'} color={'brand.yellow'}>
              {t('common.platform-restricted')}
            </Text>
          )}
        </MenuItem>
        <MenuItem onClick={logout} px={0} bg={'transparent'}>
          <Flex color={'ninja.100'} _hover={{ color: 'brand.link' }} alignItems={'center'} gap={4}>
            <Icon as={BiLogOut} boxSize={'5'} />
            <Text fontSize={'sm'}>{t('common.button.logout')}</Text>
          </Flex>
        </MenuItem>
      </MenuList>
    </Menu>
  )
}
